/**
 * Payments API service.
 *
 * Thin, typed wrappers over the shared {@link apiClient}. The API base URL
 * already includes the `/api` prefix, so paths here start at `/payments`.
 *
 * _Requirements: 3.2, 8.2, 10.4_
 */

import type { Tier, Cadence } from '@covered-calls/shared';
import { apiClient } from './api-client';

const CHECKOUT_SESSION_PATH = '/payments/checkout-session';
const PORTAL_SESSION_PATH = '/payments/portal-session';

/**
 * Create a Stripe Checkout session for a paid subscription.
 *
 * POST /api/payments/checkout-session — the backend resolves the Stripe price
 * for the given `tier`/`cadence` and returns the hosted Checkout `url` to
 * redirect the user to.
 *
 * _Requirements: 3.2, 8.2_
 */
export async function createCheckoutSession(tier: Tier, cadence: Cadence): Promise<{ url: string }> {
  return apiClient.post<{ url: string }>(CHECKOUT_SESSION_PATH, { tier, cadence });
}

/**
 * Create a Stripe Billing Portal session for the current subscriber.
 *
 * POST /api/payments/portal-session — the backend resolves the current user's
 * Stripe customer and returns the hosted Billing Portal `url` to redirect the
 * user to for managing their subscription.
 *
 * _Requirements: 10.4_
 */
export async function createPortalSession(): Promise<{ url: string }> {
  return apiClient.post<{ url: string }>(PORTAL_SESSION_PATH);
}
